import React, { useEffect, useState } from 'react'
import { EditorState } from 'draft-js'
import Button from 'components/button'
import Menu from 'components/menu'
import Select from 'components/select'
import { IContextMenuState, ITable, ITableView, ITableViewColumn } from 'types'
import { INITIAL_CONTEXT_MENU_STATE } from 'app-constants'
import { Filter } from 'components/icons'
import useProject from 'hooks/useProject'
import { getProjectTables } from 'helpers/utils'
import { useApplicationStore } from 'hooks/application'
import api from 'helpers/api'
import { groupOptionsByFolder } from 'components/sidebar/components/folders/tree'

interface SlicerInsertProps {
  editorState: EditorState
  onChange: (nextEditorState: EditorState) => void
  modifier: (
    editorState: EditorState,
    tableId: string,
    viewId: string,
    columnId: string,
    entityKey?: string
  ) => EditorState
}

export interface SlicerEditData {
  tableId: string
  viewId: string
  columnId: string
  entityKey: string
}

interface AddSlicerFormProps extends SlicerInsertProps {
  onClose: () => void
  initialData?: SlicerEditData
}

const byName = <T extends { name: string }>(a: T, b: T): number => {
  if (a.name > b.name) return 1
  if (a.name < b.name) return -1
  return 0
}

// Source column ids of SLICER entities already present in this editor's content. Slicer state is
// shared per source column (see providers/slicers), so a second slicer for the same column would
// just mirror the first — block it at insert time. When editing, pass the slicer's own entityKey
// to exclude it so keeping its current column isn't flagged as a duplicate of itself.
const getExistingSlicerColumnIds = (editorState: EditorState, excludeEntityKey?: string): Set<string> => {
  const content = editorState.getCurrentContent()
  const columnIds = new Set<string>()
  content.getBlockMap().forEach((block) => {
    const entityKey = block?.getEntityAt(0)
    if (!entityKey || entityKey === excludeEntityKey) return
    const entity = content.getEntity(entityKey)
    if (entity.getType() !== 'SLICER') return
    const existingColumnId = entity.getData()?.columnId
    if (existingColumnId) columnIds.add(existingColumnId)
  })
  return columnIds
}

const AddSlicerForm: React.FC<AddSlicerFormProps> = ({ editorState, onChange, modifier, onClose, initialData }) => {
  const { project } = useProject()
  const { displayErrorMessage } = useApplicationStore()

  const [tables, setTables] = useState<ITable[]>()
  const [tableViews, setTableViews] = useState<Array<{ name: string; publicId: string }>>([])
  const [loadingTableViews, setLoadingTableViews] = useState<boolean>(false)
  const [viewColumns, setViewColumns] = useState<ITableViewColumn[]>([])
  const [loadingViewColumns, setLoadingViewColumns] = useState<boolean>(false)

  const [tableId, setTableId] = useState<string>(initialData?.tableId ?? '')
  const [viewId, setViewId] = useState<string>(initialData?.viewId ?? '')
  const [columnId, setColumnId] = useState<string>(initialData?.columnId ?? '')

  const isEditing = !!initialData
  const isDuplicate = columnId !== '' && getExistingSlicerColumnIds(editorState, initialData?.entityKey).has(columnId)
  const isDisabled = !tableId || !viewId || !columnId || isDuplicate

  useEffect(() => {
    if (project.publicId !== '') {
      getProjectTables(project.publicId)
        .then((projectTables) => setTables(projectTables))
        .catch((e) => displayErrorMessage(e))
    }
  }, [project.publicId])

  useEffect(() => {
    if (!tableId) return

    let cancelled = false
    setLoadingTableViews(true)

    api
      .getTableViews(tableId, { ignoreColumns: true })
      .then((response) => {
        if (cancelled) return
        setTableViews(response.data.map((view: ITableView) => ({ name: view.name, publicId: view.publicId })))
      })
      .catch(() => {
        if (cancelled) return
        setTableViews([])
      })
      .finally(() => {
        if (!cancelled) setLoadingTableViews(false)
      })

    return () => {
      cancelled = true
    }
  }, [tableId])

  useEffect(() => {
    if (!viewId) {
      setViewColumns([])
      return
    }
    setLoadingViewColumns(true)
    api
      .getTableView(viewId)
      .then((response) => setViewColumns(response.data.columns ?? []))
      .catch(() => setViewColumns([]))
      .finally(() => setLoadingViewColumns(false))
  }, [viewId])

  const handleSubmit = () => {
    // The modifier returns the same EditorState instance when nothing actually changed
    // (e.g. clicking "Update Slicer" without editing anything). Only propagate onChange
    // when a new state is produced, so we don't mark the document dirty / trigger autosave.
    const nextEditorState = modifier(editorState, tableId, viewId, columnId, initialData?.entityKey)
    if (nextEditorState !== editorState) {
      onChange(nextEditorState)
    }
    onClose()
  }

  return (
    <div className="flex flex-col items-end overflow-y-auto gap-5 py-3 px-5">
      <div className="flex flex-col w-full">
        <span className="text-muted italic mb-[3px]">
          Source Table <strong className="text-red">*</strong>
        </span>
        <Select
          options={groupOptionsByFolder(
            (tables ?? []).map((table) => ({ value: table.publicId, label: table.name })),
            project.hubFolders,
            project.sidebarResources
          )}
          loading={tables === undefined}
          onOptionClick={(option) => {
            if (!tables) return
            const table = tables.find((t) => t.publicId === option)
            if (!table) return
            setTableId(table.publicId)
            setViewId(table.defaultViewId)
            setColumnId('')
          }}
          groupBy
          optionsSelected={tableId ? [tableId] : []}
        />
      </div>

      <div className="flex flex-col w-full">
        <span className="text-muted italic mb-[3px]">
          Source View <strong className="text-red">*</strong>
        </span>
        <Select
          options={tableViews
            .slice()
            .sort(byName)
            .map((view) => ({ value: view.publicId, label: view.name }))}
          loading={loadingTableViews}
          onOptionClick={(value) => {
            setViewId(value)
            setColumnId('')
          }}
          optionsSelected={viewId ? [viewId] : []}
        />
      </div>

      <div className="flex flex-col w-full">
        <span className="text-muted italic mb-[3px]">
          Source Column <strong className="text-red">*</strong>
        </span>
        <Select
          options={viewColumns
            .slice()
            .sort(byName)
            .map((column) => ({ value: column.publicId, label: column.name }))}
          loading={loadingViewColumns}
          onOptionClick={setColumnId}
          optionsSelected={columnId ? [columnId] : []}
        />
        {isDuplicate && (
          <span className="text-red mt-[3px]">A slicer for this column already exists in this section.</span>
        )}
      </div>

      <Button className="ml-[7px] h-[45px]" onClick={handleSubmit} internalType="accept" disabled={isDisabled}>
        {isEditing ? 'Update Slicer' : 'Insert Slicer'}
      </Button>
    </div>
  )
}

const SlicerInsert: React.FC<SlicerInsertProps> = ({ editorState, onChange, modifier }) => {
  const [slicerMenu, setSlicerMenu] = useState<IContextMenuState>(INITIAL_CONTEXT_MENU_STATE)

  return (
    <div>
      <div
        title="Add Slicer"
        className="flex text-base text-body items-center h-full cursor-pointer hover:bg-surface-sunken select-none"
        onClick={(event) =>
          setSlicerMenu({
            open: true,
            top: `${event.currentTarget.getBoundingClientRect().bottom + 10}px`,
            left: `${event.currentTarget.getBoundingClientRect().left - 400 + 500}px`,
            right: 'auto',
            bottom: 'auto'
          })
        }
      >
        <Filter />
        <span className="ml-[5px]">Slicer</span>
      </div>
      {slicerMenu.open && (
        <Menu id="slicer-insert-menu" menuState={slicerMenu} setMenuState={setSlicerMenu} width={400} zIndex={1000}>
          <AddSlicerForm
            editorState={editorState}
            onChange={onChange}
            onClose={() => setSlicerMenu(INITIAL_CONTEXT_MENU_STATE)}
            modifier={modifier}
          />
        </Menu>
      )}
    </div>
  )
}

export { AddSlicerForm }
export default SlicerInsert
